'use client';

import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { ROUTES } from '../../../constants/routes';
import { cn } from '../../../lib/utils';
import { themeConfig } from '../../../theme';

export type BrandLogoLayout = 'icon' | 'horizontal';

export type BrandLogoProps = {
  to?: string;
  className?: string;
  ariaLabel?: string;
  /** Icon-only (e.g. collapsed sidebar). */
  compact?: boolean;
  size?: 'sm' | 'md';
  /** Icon + optional wordmark, or full horizontal lockup (admin console). */
  layout?: BrandLogoLayout;
  /** When set, shown next to the icon instead of `themeConfig.brand.shortName`. */
  wordmark?: string;
  /**
   * When set (e.g. short-lived signed shelter logo URL), replaces the default product icon.
   * Falls back to the default icon if the image fails to load.
   */
  customBrandImageSrc?: string | null;
  /**
   * Horizontal lockup for expanded sidebar (`layout="horizontal"`).
   * Defaults to `themeConfig.logos.brandingHorizontal`.
   */
  horizontalLogoSrc?: string;
};

const imgSize = {
  sm: 'size-7',
  md: 'size-9',
} as const;

const textSize = {
  sm: 'text-base',
  md: 'text-lg',
} as const;

export function BrandLogo({
  to = ROUTES.admin,
  className,
  ariaLabel,
  compact = false,
  size = 'md',
  layout = 'icon',
  wordmark,
  customBrandImageSrc,
  horizontalLogoSrc = themeConfig.logos.brandingHorizontal,
}: BrandLogoProps) {
  const displayWordmark = wordmark ?? themeConfig.brand.shortName;
  const [customFailed, setCustomFailed] = useState(false);
  const [horizontalFailed, setHorizontalFailed] = useState(false);

  useEffect(() => {
    setCustomFailed(false);
  }, [customBrandImageSrc]);

  useEffect(() => {
    setHorizontalFailed(false);
  }, [horizontalLogoSrc]);

  const showCustom =
    typeof customBrandImageSrc === 'string' &&
    customBrandImageSrc.length > 0 &&
    !customFailed;

  const iconSrc = showCustom ? customBrandImageSrc : themeConfig.logos.icon;
  const useHorizontalLockup = layout === 'horizontal' && !showCustom;

  const showHorizontalExpanded =
    useHorizontalLockup && !compact && !horizontalFailed;

  const resolvedLabel =
    ariaLabel ??
    (showHorizontalExpanded
      ? `${themeConfig.brand.shortName} admin home`
      : `${displayWordmark} admin home`);
  const isExternalHref = /^https?:\/\//i.test(to);
  const sharedClassName = cn(
    'flex min-w-0 items-center rounded-lg text-foreground no-underline outline-none transition-colors hover:bg-accent/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
    compact ? 'justify-center p-1' : 'gap-2.5 px-1 py-0.5',
    className,
  );
  const content = (
    <>
      {showHorizontalExpanded ? (
        <img
          src={horizontalLogoSrc}
          alt={themeConfig.brand.shortName}
          width={200}
          height={48}
          className='h-8 w-auto max-w-full object-contain object-left'
          onError={() => setHorizontalFailed(true)}
        />
      ) : (
        <img
          src={iconSrc}
          alt=''
          width={36}
          height={36}
          className={cn(
            'shrink-0 rounded-lg object-cover',
            imgSize[size],
          )}
          onError={() => {
            if (showCustom) setCustomFailed(true);
          }}
        />
      )}
      {compact || showHorizontalExpanded ? null : (
        <span
          className={cn(
            'min-w-0 truncate font-heading font-semibold tracking-tight text-foreground',
            textSize[size],
          )}
        >
          {displayWordmark}
        </span>
      )}
    </>
  );

  if (isExternalHref) {
    return (
      <a href={to} className={sharedClassName} aria-label={resolvedLabel}>
        {content}
      </a>
    );
  }

  return (
    <NextLink href={to} className={sharedClassName} aria-label={resolvedLabel}>
      {content}
    </NextLink>
  );
}
